import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import {
  useIsDelegatee,
  useModuleVisible,
  usePermission,
  usePermissionsHydrated,
} from '@core/permissions/usePermission';
import { FullPageSpinner } from '@ui/Spinner';
import type { PermissionAction, PermissionGroup } from '@core/permissions/actions';

/**
 * Route-level permission gate. NEW — the Angular app had no permission guard at all.
 *
 * Authorization was cosmetic: nav links were hidden, but typing the URL reached the
 * page. `/ai-search` did not even have `authGuard`. This closes both gaps on the client;
 * the server remains the real authority.
 *
 * Waits for permissions to hydrate before deciding, so a refresh on a permitted page
 * does not bounce the user to the dashboard while the store is still empty. Verified with a
 * restricted account: a hard load onto a forbidden URL fired NO request from the guarded page,
 * proving it never mounted even for a frame.
 *
 * ============================================================================
 * `action` OR `module` — the API expresses authorization both ways
 * ============================================================================
 *
 * `GET /users/api/permissions` returns individual ACTIONS (`role:view`) and GROUPS
 * (`vendor_approvals: { visible }`). The sidebar already gates on both, because Angular's
 * `main-layout.component.html` did: some links used `hasPermission(action)` and the Approval
 * Workflow link used `isModuleVisible('vendor_approvals')`.
 *
 * The `module` form was added after testing with a restricted account showed the sidebar hiding
 * Approval Workflow while `/approval-workflow` stayed reachable by URL — the app contradicting
 * itself, and the exact gap this component exists to close.
 *
 * Exactly one of the two must be supplied.
 */
type RequirePermissionProps = {
  children: ReactNode;
  redirectTo?: string;
  /**
   * Also admit a user who is a DELEGATEE, whatever `action`/`module` says.
   *
   * ⚠️ NEEDED BY `/requests`, and its absence would be a trap: that route is gated on
   * `vendor:approve`, so a delegatee holding no approval permission of their own would be bounced
   * to the dashboard by the very queue the delegation exists to give them. The page's own row
   * actions already widen the same way — see `canApproveWorkflows`.
   */
  orDelegatee?: boolean;
} & (
  | { action: PermissionAction; module?: never }
  | { module: PermissionGroup; action?: never }
);

export function RequirePermission({
  action,
  module,
  children,
  orDelegatee = false,
  redirectTo = '/dashboard',
}: RequirePermissionProps) {
  const hydrated = usePermissionsHydrated();
  // Every hook runs unconditionally — they are hooks, so they cannot be called behind a branch.
  // `undefined` makes each check a no-op that reports `true`, so an unused one never denies.
  const actionAllowed = usePermission(action);
  const moduleAllowed = useModuleVisible(module);
  const delegatee = useIsDelegatee();

  if (!hydrated) return <FullPageSpinner />;

  // The delegation is a whole-gate override rather than one more term in the AND: a delegatee is
  // admitted even though they hold neither the action nor the module.
  const allowed = (actionAllowed && moduleAllowed) || (orDelegatee && delegatee);
  if (!allowed) return <Navigate to={redirectTo} replace />;

  return <>{children}</>;
}
